import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { analytics, ANALYTICS_EVENTS } from '@/lib/analytics';
import type { 
  Milestone, 
  MilestoneApproval, 
  MilestoneComment,
  CreateMilestoneData,
  UpdateMilestoneData,
  RequestMilestoneChangeData,
  MilestoneApprovalStatus
} from '@/lib/types/milestone';

/**
 * Hook for fetching milestones associated with a specific job
 * 
 * Milestones represent key deliverable points in a job's lifecycle.
 * Each milestone has an action owner (client/vendor), due date, and completion status.
 * 
 * @param jobId - The unique identifier of the job to fetch milestones for
 * @returns Object containing milestones array, loading state, error, and refetch function
 * 
 * @example
 * ```typescript
 * const { milestones, loading, error, refetch } = useMilestones(jobId);
 * if (!loading && milestones.length > 0) {
 *   console.log('First milestone:', milestones[0].milestone_name);
 * }
 * ```
 */
export const useMilestones = (jobId?: string) => {
  const [milestones, setMilestones] = useState<Milestone[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchMilestones = async () => {
    if (!jobId) return;

    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('milestones')
        .select('*')
        .eq('job_id', jobId)
        .order('milestone_order', { ascending: true });

      if (error) throw error;
      setMilestones(data as Milestone[] || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch milestones');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMilestones();
  }, [jobId]);

  return {
    milestones,
    loading,
    error,
    refetch: fetchMilestones
  };
};

/**
 * Hook for creating new milestones on a job
 * 
 * This hook handles milestone creation with automatic approval management:
 * - When a client creates a milestone, vendor approval is removed (if exists)
 * - When a vendor creates a milestone, client approval is removed (if exists)
 * - Sends notifications to the opposing party about the new milestone
 * 
 * @returns Object with createMilestone function and loading state
 * 
 * @example
 * ```typescript
 * const { createMilestone, loading } = useCreateMilestone();
 * await createMilestone(jobId, {
 *   milestone_name: 'Design Review',
 *   due_date: '2024-12-01',
 *   milestone_type: 'review',
 *   deliverable: 'Design mockups',
 *   action_owner: 'vendor',
 *   milestone_order: 1
 * });
 * ```
 */
export const useCreateMilestone = () => {
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const createMilestone = async (jobId: string, milestoneData: CreateMilestoneData) => {
    try {
      setLoading(true);

      // Get current user to determine if they're client or vendor
      const user = await supabase.auth.getUser();
      if (!user.data.user) throw new Error('Not authenticated');

      // Get user's organization
      const { data: userProfile } = await supabase
        .from('profiles')
        .select('organization_id')
        .eq('user_id', user.data.user.id)
        .single();

      // Get job details for organization_id
      const { data: job, error: jobError } = await supabase
        .from('jobs')
        .select('organization_id')
        .eq('id', jobId)
        .single();

      if (jobError) throw jobError;

      // User is a client if their org matches the job's org
      const isClient = userProfile?.organization_id === job?.organization_id;

      const { data, error } = await supabase
        .from('milestones')
        .insert({
          job_id: jobId,
          organization_id: job.organization_id,
          created_by_user_id: user.data.user.id,
          last_updated_by_user_id: user.data.user.id,
          ...milestoneData
        })
        .select()
        .single();

      if (error) throw error;

      // Check if there's an existing approval and remove opposing party's approval
      const { data: existingApproval } = await supabase
        .from('milestone_approvals')
        .select('*')
        .eq('job_id', jobId)
        .single();

      if (existingApproval) {
        let newApprovalData: any = {};

        if (isClient) {
          // Client added milestone, remove vendor approval
          if (existingApproval.vendor_approved_at) {
            newApprovalData = {
              vendor_approved_at: null,
              vendor_approved_by_user_id: null,
              approval_status: existingApproval.client_approved_at ? 'approved_by_client' : 'pending_both'
            };
          }
        } else {
          // Vendor added milestone, remove client approval
          if (existingApproval.client_approved_at) {
            newApprovalData = {
              client_approved_at: null,
              client_approved_by_user_id: null,
              approval_status: existingApproval.vendor_approved_at ? 'approved_by_vendor' : 'pending_both'
            };
          }
        }

        // Update approval if changes were detected
        if (Object.keys(newApprovalData).length > 0) {
          await supabase
            .from('milestone_approvals')
            .update(newApprovalData)
            .eq('job_id', jobId);
        }
      }

      // Notify organization and vendor if job is awarded
      const { data: jobWithVendor } = await supabase
        .from('jobs')
        .select('public_id, title, organization_id, status')
        .eq('id', jobId)
        .single();

      if (jobWithVendor) {
        // Notify via edge function
        await supabase.functions.invoke('milestone-notify', {
          body: { type: 'create', jobId, milestoneName: milestoneData.milestone_name },
        });
      }

      toast({
        title: "Milestone Created",
        description: `Milestone "${milestoneData.milestone_name}" has been created.`
      });

      return data;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create milestone';
      toast({
        title: "Error",
        description: message,
        variant: "destructive"
      });
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return { createMilestone, loading };
};

/**
 * Hook for updating existing milestones
 * 
 * Updates trigger approval resets similar to milestone creation:
 * - Client updates remove vendor approval
 * - Vendor updates remove client approval
 * - Ensures both parties review any changes made to the timeline
 * 
 * @returns Object with updateMilestone function and loading state
 * 
 * @example
 * ```typescript
 * const { updateMilestone, loading } = useUpdateMilestone();
 * await updateMilestone(milestoneId, {
 *   due_date: '2024-12-15', // Extended deadline
 *   description: 'Updated design requirements'
 * });
 * ```
 */
export const useUpdateMilestone = () => {
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const updateMilestone = async (milestoneId: string, milestoneData: UpdateMilestoneData) => {
    try {
      setLoading(true);

      // Get the milestone to find the job_id
      const { data: milestone, error: milestoneError } = await supabase
        .from('milestones')
        .select('job_id, organization_id')
        .eq('id', milestoneId)
        .single();

      if (milestoneError) throw milestoneError;

      // Get current user to determine if they're client or vendor
      const user = await supabase.auth.getUser();
      if (!user.data.user) throw new Error('Not authenticated');

      // Get user's organization
      const { data: userProfile } = await supabase
        .from('profiles')
        .select('organization_id')
        .eq('user_id', user.data.user.id)
        .single();

      const { data: job } = await supabase
        .from('jobs')
        .select('organization_id')
        .eq('id', milestone.job_id)
        .single();

      // User is a client if their org matches the job's org
      const isClient = userProfile?.organization_id === job?.organization_id;
      
      // Update the milestone
      const { data, error } = await supabase
        .from('milestones')
        .update({
          ...milestoneData,
          last_updated_by_user_id: user.data.user.id
        })
        .eq('id', milestoneId)
        .select()
        .single();

      if (error) throw error;

      // Sync "Final Delivery Due" milestone date back to job due_date
      if (data.milestone_name === 'Final Delivery Due' && 
          data.is_anchor_milestone === true && 
          milestoneData.due_date) {
        
        const { error: jobUpdateError } = await supabase
          .from('jobs')
          .update({ due_date: milestoneData.due_date })
          .eq('id', milestone.job_id);
        
        if (jobUpdateError) {
          console.error('Failed to sync job due_date with milestone:', jobUpdateError);
          // Don't throw - milestone update succeeded, log the sync failure
        }
      }

      // Check if there's an existing approval and remove opposing party's approval
      const { data: existingApproval } = await supabase
        .from('milestone_approvals')
        .select('*')
        .eq('job_id', milestone.job_id)
        .single();

      if (existingApproval) {
        let newApprovalData: any = {};
        let newStatus: MilestoneApprovalStatus = 'pending_both';

        if (isClient) {
          // Client made changes, remove vendor approval
          if (existingApproval.vendor_approved_at) {
            newApprovalData = {
              vendor_approved_at: null,
              vendor_approved_by_user_id: null,
              approval_status: existingApproval.client_approved_at ? 'approved_by_client' : 'pending_both'
            };
          }
        } else {
          // Vendor made changes, remove client approval
          if (existingApproval.client_approved_at) {
            newApprovalData = {
              client_approved_at: null,
              client_approved_by_user_id: null,
              approval_status: existingApproval.vendor_approved_at ? 'approved_by_vendor' : 'pending_both'
            };
          }
        }

        // Update approval if changes were detected
        if (Object.keys(newApprovalData).length > 0) {
          await supabase
            .from('milestone_approvals')
            .update(newApprovalData)
            .eq('job_id', milestone.job_id);
        }
      }

      toast({
        title: "Milestone Updated",
        description: "Milestone has been updated successfully."
      });

      return data;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update milestone';
      toast({
        title: "Error",
        description: message,
        variant: "destructive"
      });
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return { updateMilestone, loading };
};

/**
 * Hook for fetching the approval status of a job's milestone timeline
 * 
 * Milestone approvals track whether both the client and vendor have signed off
 * on the proposed milestone schedule. Both parties must approve before work begins.
 * 
 * @param jobId - The unique identifier of the job
 * @returns Object containing approval data, loading state, error, and refetch function
 * 
 * @example
 * ```typescript
 * const { approval, loading } = useMilestoneApproval(jobId);
 * if (approval?.approval_status === 'approved_by_both') {
 *   console.log('Timeline approved by both parties');
 * }
 * ```
 */
export const useMilestoneApproval = (jobId?: string) => {
  const [approval, setApproval] = useState<MilestoneApproval | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchApproval = async () => {
    if (!jobId) return;

    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('milestone_approvals')
        .select('*')
        .eq('job_id', jobId)
        .maybeSingle();

      if (error) throw error;
      setApproval(data as MilestoneApproval || null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch milestone approval');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchApproval();
  }, [jobId]);

  return {
    approval,
    loading,
    error,
    refetch: fetchApproval
  };
};

/**
 * Hook for approving or rejecting a job's milestone timeline
 * 
 * Handles approval logic for both clients and vendors:
 * - Creates or updates the approval record in the database
 * - Tracks which party approved and when
 * - Determines if both parties have now approved (approval_status = 'approved_by_both')
 * - Sends notifications to the opposing party
 * 
 * @returns Object with approveMilestones function and loading state
 * 
 * @example
 * ```typescript
 * const { approveMilestones, loading } = useApproveMilestones();
 * // Client approves the timeline
 * await approveMilestones(jobId, 'client');
 * // Once vendor also approves, status becomes 'approved_by_both'
 * ```
 */
export const useApproveMilestones = () => {
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const approveMilestones = async (jobId: string, approvalType: 'client' | 'vendor') => {
    try {
      setLoading(true);

      // Get current user and job details
      const user = await supabase.auth.getUser();
      if (!user.data.user) throw new Error('Not authenticated');

      const { data: job, error: jobError } = await supabase
        .from('jobs')
        .select('organization_id')
        .eq('id', jobId)
        .single();

      if (jobError) throw jobError;

      // Check if approval record exists
      const { data: existingApproval } = await supabase
        .from('milestone_approvals')
        .select('*')
        .eq('job_id', jobId)
        .single();

      let approvalData;
      if (approvalType === 'client') {
        approvalData = {
          client_approved_at: new Date().toISOString(),
          client_approved_by_user_id: user.data.user.id,
          approval_status: existingApproval?.vendor_approved_at ? 'approved_by_both' : 'approved_by_client'
        };
      } else {
        approvalData = {
          vendor_approved_at: new Date().toISOString(),
          vendor_approved_by_user_id: user.data.user.id,
          approval_status: existingApproval?.client_approved_at ? 'approved_by_both' : 'approved_by_vendor'
        };
      }

      if (existingApproval) {
        // Update existing approval
        const { error } = await supabase
          .from('milestone_approvals')
          .update(approvalData)
          .eq('job_id', jobId);

        if (error) throw error;
      } else {
        // Create new approval record
        const { error } = await supabase
          .from('milestone_approvals')
          .insert({
            job_id: jobId,
            organization_id: job.organization_id,
            ...approvalData
          });

        if (error) throw error;
      }

      // Notify the other party
      const { data: jobDetails } = await supabase
        .from('jobs')
        .select('id, public_id, title, organization_id')
        .eq('id', jobId)
        .single();

      if (jobDetails) {
        // Notify via edge function
        await supabase.functions.invoke('milestone-notify', {
          body: { type: 'approve', jobId, approvalType },
        });
      }

      toast({
        title: "Milestones Approved",
        description: `You have approved the milestone timeline as ${approvalType}.`
      });

    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to approve milestones';
      toast({
        title: "Error",
        description: message,
        variant: "destructive"
      });
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return { approveMilestones, loading };
};

/**
 * Hook for fetching comments on a specific milestone
 * 
 * Comments allow clients and vendors to discuss specific milestones,
 * ask questions, provide clarifications, or document decisions.
 * 
 * @param milestoneId - The unique identifier of the milestone
 * @returns Object containing comments array, loading state, error, and refetch function
 * 
 * @example
 * ```typescript
 * const { comments, loading, refetch } = useMilestoneComments(milestoneId);
 * // Display comments in chronological order
 * comments.forEach(c => console.log(`${c.comment_text} at ${c.created_at}`));
 * ```
 */
export const useMilestoneComments = (milestoneId?: string) => {
  const [comments, setComments] = useState<MilestoneComment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchComments = async () => {
    if (!milestoneId) return;

    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('milestone_comments')
        .select('*')
        .eq('milestone_id', milestoneId)
        .order('created_at', { ascending: true });

      if (error) throw error;
      setComments(data as MilestoneComment[] || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch comments');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchComments();
  }, [milestoneId]);

  return {
    comments,
    loading,
    error,
    refetch: fetchComments
  };
};

/**
 * Hook for creating new comments on a milestone
 * 
 * Allows authenticated users to add discussion comments to any milestone.
 * Comments are associated with the user's organization and tracked by timestamp.
 * 
 * @returns Object with createComment function and loading state
 * 
 * @example
 * ```typescript
 * const { createComment, loading } = useCreateMilestoneComment();
 * await createComment(milestoneId, 'Can we extend this deadline by 2 days?');
 * ```
 */
export const useCreateMilestoneComment = () => {
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const createComment = async (milestoneId: string, commentText: string) => {
    try {
      setLoading(true);

      // Get milestone details for organization_id
      const { data: milestone, error: milestoneError } = await supabase
        .from('milestones')
        .select('organization_id')
        .eq('id', milestoneId)
        .single();

      if (milestoneError) throw milestoneError;

      const { data, error } = await supabase
        .from('milestone_comments')
        .insert({
          milestone_id: milestoneId,
          organization_id: milestone.organization_id,
          commenter_user_id: (await supabase.auth.getUser()).data.user?.id,
          comment_text: commentText
        })
        .select()
        .single();

      if (error) throw error;

      toast({
        title: "Comment Added",
        description: "Your comment has been added to the milestone."
      });

      return data;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to add comment';
      toast({
        title: "Error",
        description: message,
        variant: "destructive"
      });
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return { createComment, loading };
};

/**
 * Hook for requesting changes to an existing milestone
 * 
 * Allows clients or vendors to propose modifications to milestones. Automatically:
 * - Records change history for audit trails
 * - Updates approval status to 'change_requested_by_[party]'
 * - Notifies the opposing party of the requested changes
 * - Increments milestone version number
 * 
 * @returns Object with requestChange function and loading state
 * 
 * @example
 * ```typescript
 * const { requestChange, loading } = useRequestMilestoneChange();
 * await requestChange(milestoneId, {
 *   due_date: '2024-12-20',
 *   change_reason: 'Need additional time for material sourcing'
 * }, 'vendor');
 * ```
 */
export const useRequestMilestoneChange = () => {
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const requestChange = async (
    milestoneId: string, 
    changeData: RequestMilestoneChangeData, 
    requestorType: 'client' | 'vendor',
    onSuccess?: () => void
  ) => {
    try {
      setLoading(true);

      // Extract change_reason from changeData
      const { change_reason, ...pendingChanges } = changeData;

      // Get the current milestone
      const { data: currentMilestone, error: currentError } = await supabase
        .from('milestones')
        .select('*')
        .eq('id', milestoneId)
        .single();

      if (currentError) throw currentError;

      const user = await supabase.auth.getUser();
      if (!user.data.user) throw new Error('Not authenticated');

      // Create pending change record instead of updating milestone
      const { error: pendingError } = await supabase
        .from('milestone_pending_changes')
        .insert({
          milestone_id: milestoneId,
          job_id: currentMilestone.job_id,
          organization_id: currentMilestone.organization_id,
          requested_by_user_id: user.data.user.id,
          requested_by_type: requestorType,
          change_reason: change_reason || null,
          pending_changes: pendingChanges,
          status: 'pending'
        });

      if (pendingError) throw pendingError;

      // Update the milestone approval status to reflect the change request
      const changeRequestedStatus: 'change_requested_by_client' | 'change_requested_by_vendor' = requestorType === 'client' 
        ? 'change_requested_by_client' 
        : 'change_requested_by_vendor';

      // When requesting a change, the requestor is implicitly approving it
      // So we set their approval and only reset the other party's approval
      const approvalUpdateData = requestorType === 'client'
        ? {
            approval_status: changeRequestedStatus,
            // Client is requesting/approving the change
            client_approved_at: new Date().toISOString(),
            client_approved_by_user_id: user.data.user.id,
            // Reset vendor's approval since they need to review
            vendor_approved_at: null,
            vendor_approved_by_user_id: null
          }
        : {
            approval_status: changeRequestedStatus,
            // Vendor is requesting/approving the change
            vendor_approved_at: new Date().toISOString(),
            vendor_approved_by_user_id: user.data.user.id,
            // Reset client's approval since they need to review
            client_approved_at: null,
            client_approved_by_user_id: null
          };

      const { error: approvalUpdateError } = await supabase
        .from('milestone_approvals')
        .update(approvalUpdateData)
        .eq('job_id', currentMilestone.job_id);

      if (approvalUpdateError) {
        console.error('Error updating approval status:', approvalUpdateError);
        // Don't throw - the pending change is already created
      }

      // Notify the other party
      await supabase.functions.invoke('milestone-notify', {
        body: { type: 'change_request', jobId: currentMilestone.job_id, requestorType },
      });

      toast({
        title: "Change Requested",
        description: "Your change request has been submitted and requires approval from the other party."
      });

      onSuccess?.();

    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to request change';
      toast({
        title: "Error",
        description: message,
        variant: "destructive"
      });
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return { requestChange, loading };
};

/**
 * Hook for fetching the change history of a milestone
 * 
 * Returns all documented changes made to a milestone over time, including:
 * - Field-level changes (due_date, description, etc.)
 * - Who made the change and when
 * - Reason for the change if provided
 * 
 * Useful for audit trails and understanding milestone evolution.
 * 
 * @param milestoneId - The unique identifier of the milestone
 * @returns Object containing change history array, loading state, error, and refetch function
 * 
 * @example
 * ```typescript
 * const { changeHistory, loading } = useMilestoneChangeHistory(milestoneId);
 * changeHistory.forEach(change => {
 *   console.log(`${change.field_name}: ${change.old_value} → ${change.new_value}`);
 * });
 * ```
 */
export const useMilestoneChangeHistory = (milestoneId?: string) => {
  const [changeHistory, setChangeHistory] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchChangeHistory = async () => {
    if (!milestoneId) return;

    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('milestone_change_history')
        .select('*')
        .eq('milestone_id', milestoneId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setChangeHistory(data || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch change history');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchChangeHistory();
  }, [milestoneId]);

  return {
    changeHistory,
    loading,
    error,
    refetch: fetchChangeHistory
  };
};

/**
 * Hook for responding to milestone change requests
 * 
 * Allows the receiving party to approve or decline proposed milestone changes.
 * - Approve: Sets approval status to 'approved_by_[party]' and updates timestamps
 * - Decline: Resets to 'pending_both' status, requiring renegotiation
 * 
 * @returns Object with respondToChange function and loading state
 * 
 * @example
 * ```typescript
 * const { respondToChange, loading } = useRespondToChangeRequest();
 * // Client approves vendor's requested change
 * await respondToChange(jobId, 'approve', 'client');
 * ```
 */
export const useRespondToChangeRequest = () => {
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const respondToChange = async (
    jobId: string, 
    response: 'approve' | 'decline',
    responderType: 'client' | 'vendor',
    onSuccess?: () => void
  ) => {
    try {
      setLoading(true);

      const user = await supabase.auth.getUser();
      if (!user.data.user) throw new Error('Not authenticated');

      if (response === 'approve') {
        // Get all pending changes for this job
        const { data: pendingChanges, error: pendingError } = await supabase
          .from('milestone_pending_changes')
          .select('*')
          .eq('job_id', jobId)
          .eq('status', 'pending');

        if (pendingError) throw pendingError;

        // Apply each pending change to its milestone
        for (const change of pendingChanges || []) {
          // Get current milestone for history tracking
          const { data: currentMilestone } = await supabase
            .from('milestones')
            .select('*')
            .eq('id', change.milestone_id)
            .single();

          // Update the milestone with pending changes
          const { error: updateError, data: updatedMilestone } = await supabase
            .from('milestones')
            .update({
              ...(change.pending_changes as Record<string, any>),
              last_updated_by_user_id: user.data.user.id
            })
            .eq('id', change.milestone_id)
            .select()
            .single();

          if (updateError) throw updateError;

          // Sync "Final Delivery Due" milestone date back to job due_date
          if (updatedMilestone?.milestone_name === 'Final Delivery Due' && 
              updatedMilestone?.is_anchor_milestone === true && 
              change.pending_changes['due_date']) {
            
            const { error: jobUpdateError } = await supabase
              .from('jobs')
              .update({ due_date: change.pending_changes['due_date'] })
              .eq('id', jobId);
            
            if (jobUpdateError) {
              console.error('Error syncing Final Delivery Due to job due_date:', jobUpdateError);
            } else {
              console.log('Successfully synced Final Delivery Due to job due_date');
            }
          }

          // Record change history for audit trail
          const changeEntries = [];
          for (const [fieldName, newValue] of Object.entries(change.pending_changes)) {
            const oldValue = currentMilestone?.[fieldName];
            if (oldValue !== newValue) {
              changeEntries.push({
                milestone_id: change.milestone_id,
                organization_id: change.organization_id,
                changed_by_user_id: user.data.user.id,
                change_type: 'field_change',
                field_name: fieldName,
                old_value: String(oldValue || ''),
                new_value: String(newValue || ''),
                change_reason: change.change_reason || null
              });
            }
          }

          if (changeEntries.length > 0) {
            await supabase.from('milestone_change_history').insert(changeEntries);
          }

          // Mark pending change as approved
          await supabase
            .from('milestone_pending_changes')
            .update({
              status: 'approved',
              responded_by_user_id: user.data.user.id,
              responded_at: new Date().toISOString()
            })
            .eq('id', change.id);
        }

        // Fetch current approval status to check if other party already approved
        const { data: existingApproval } = await supabase
          .from('milestone_approvals')
          .select('client_approved_at, vendor_approved_at')
          .eq('job_id', jobId)
          .single();

        // Determine if both parties have now approved
        const bothApproved = responderType === 'client'
          ? existingApproval?.vendor_approved_at != null // Vendor already approved (was requestor)
          : existingApproval?.client_approved_at != null; // Client already approved (was requestor)

        // Update approval status
        const approvalData = responderType === 'client' 
          ? {
              client_approved_at: new Date().toISOString(),
              client_approved_by_user_id: user.data.user.id,
              approval_status: bothApproved ? 'approved_by_both' as const : 'approved_by_client' as const
            }
          : {
              vendor_approved_at: new Date().toISOString(),
              vendor_approved_by_user_id: user.data.user.id,
              approval_status: bothApproved ? 'approved_by_both' as const : 'approved_by_vendor' as const
            };

        await supabase
          .from('milestone_approvals')
          .update(approvalData)
          .eq('job_id', jobId);

      } else {
        // Decline - mark all pending changes as declined
        await supabase
          .from('milestone_pending_changes')
          .update({
            status: 'declined',
            responded_by_user_id: user.data.user.id,
            responded_at: new Date().toISOString()
          })
          .eq('job_id', jobId)
          .eq('status', 'pending');

        // Reset approval status and clear all approval timestamps
        await supabase
          .from('milestone_approvals')
          .update({ 
            approval_status: 'pending_both',
            client_approved_at: null,
            client_approved_by_user_id: null,
            vendor_approved_at: null,
            vendor_approved_by_user_id: null
          })
          .eq('job_id', jobId);
      }

      // Notify the initiating party about the response
      try {
        const { error: notifyError } = await supabase.functions.invoke('milestone-notify', {
          body: { 
            type: 'change_request_response', 
            jobId, 
            responderType, 
            response 
          },
        });
        if (notifyError) {
          console.error('Error sending change request response notification:', notifyError);
        }
      } catch (notifyErr) {
        console.error('Error invoking milestone-notify for change request response:', notifyErr);
      }

      toast({
        title: response === 'approve' ? "Change Approved" : "Change Declined",
        description: response === 'approve' 
          ? "The requested changes have been applied to the milestones." 
          : "The requested changes have been declined."
      });

      onSuccess?.();

    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to respond to change request';
      toast({
        title: "Error",
        description: message,
        variant: "destructive"
      });
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return { respondToChange, loading };
};

/**
 * Hook for fetching pending milestone changes for a job
 */
export const usePendingMilestoneChanges = (jobId: string) => {
  const [pendingChanges, setPendingChanges] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchPendingChanges = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('milestone_pending_changes')
        .select(`
          *,
          milestones:milestone_id (
            milestone_name,
            milestone_order
          )
        `)
        .eq('job_id', jobId)
        .eq('status', 'pending')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setPendingChanges(data || []);
    } catch (error) {
      console.error('Error fetching pending changes:', error);
      setPendingChanges([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (jobId) {
      fetchPendingChanges();
    }
  }, [jobId]);

  return { pendingChanges, loading, refetch: fetchPendingChanges };
};

/**
 * Hook for marking a milestone as complete
 * 
 * Records milestone completion with timestamp and completing user.
 * Typically triggered by the action_owner when deliverable is satisfied.
 * 
 * @returns Object with markComplete function and loading state
 * 
 * @example
 * ```typescript
 * const { markComplete, loading } = useMarkMilestoneComplete();
 * await markComplete(milestoneId);
 * // Milestone now shows as completed in all views
 * ```
 */
export const useMarkMilestoneComplete = () => {
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const markComplete = async (milestoneId: string) => {
    try {
      setLoading(true);

      const currentUserId = (await supabase.auth.getUser()).data.user?.id;

      // First fetch the milestone to get job_id and check if it's a system milestone
      const { data: milestone, error: fetchError } = await supabase
        .from('milestones')
        .select('job_id, is_system_milestone')
        .eq('id', milestoneId)
        .single();

      if (fetchError) throw fetchError;

      const { error } = await supabase
        .from('milestones')
        .update({
          is_completed: true,
          completed_at: new Date().toISOString(),
          completed_by_user_id: currentUserId,
          updated_at: new Date().toISOString()
        })
        .eq('id', milestoneId);

      if (error) throw error;

      // Send notification for user milestones (not system milestones)
      if (!milestone.is_system_milestone && milestone.job_id) {
        try {
          // Determine if current user is client or vendor
          const { data: job } = await supabase
            .from('jobs')
            .select('organization_id')
            .eq('id', milestone.job_id)
            .single();

          const { data: profile } = await supabase
            .from('profiles')
            .select('organization_id')
            .eq('user_id', currentUserId)
            .single();

          const isClient = job?.organization_id === profile?.organization_id;
          const completedByRole = isClient ? 'client' : 'vendor';

          console.log('Sending milestone completion notification:', {
            jobId: milestone.job_id,
            milestoneId,
            completedByRole,
            isSystemMilestone: milestone.is_system_milestone
          });

          const { data: notifyResult, error: notifyError } = await supabase.functions.invoke('milestone-notify', {
            body: {
              type: 'complete',
              jobId: milestone.job_id,
              milestoneId: milestoneId,
              completedByRole
            }
          });

          if (notifyError) {
            console.error('Error sending milestone completion notification:', notifyError);
            console.error('Notification payload:', { 
              type: 'complete', 
              jobId: milestone.job_id, 
              milestoneId, 
              completedByRole 
            });
          } else {
            console.log('Milestone completion notification sent successfully:', notifyResult);
          }
        } catch (notifyErr) {
          console.error('Exception sending milestone completion notification:', notifyErr);
          // Don't fail the operation if notification fails
        }
      }

      // Track milestone completion
      analytics.track({
        name: ANALYTICS_EVENTS.MILESTONE_MARKED_COMPLETE,
        category: 'engagement',
        properties: {
          milestone_id: milestoneId,
          job_id: milestone.job_id,
        },
      });

      toast({
        title: "Success",
        description: "Milestone marked as complete",
      });

      return { success: true };
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to mark milestone complete';
      toast({
        title: "Error",
        description: message,
        variant: "destructive"
      });
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return { markComplete, loading };
};